
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"

import { set, z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AuthLogin, FetchMe } from "./ApiRoutes"
import { useContext, useState } from "react"
import { toast, Toaster } from "sonner"
import { Link, useNavigate } from "react-router-dom"
import { Check } from "lucide-react"
import { UserContext } from "@/App"
import RegisterDialog from "./RegisterDialog"

const formSchema = z.object({
    username: z.string().min(3, {
        message:
        "Username must be at least 3 characters long"
        }).max(50, {
        message: "Username must be at most 50 characters long"
    }),
    
    password: z.string()

})

interface LoginDialogProps {
    children: React.ReactNode;
    open?: boolean;
}
export default function LoginDialog({children, open=false}: LoginDialogProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(open);
  let isAuth = false;
  const {user,setUser} = useContext(UserContext);

  const {isPending: isPending, isError, data: profileData, isSuccess, error } = useQuery({
          queryKey: ['profile', localStorage.getItem('username')],
          queryFn: () => FetchMe(),
          enabled: !!isAuth,
      });
  
  if (isSuccess && profileData) {
    setUser(profileData);
  }

  const postLogin = useMutation({
        mutationFn: (variables) => 
            AuthLogin(variables.username, variables.password),

        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['login', variables.username] });
            localStorage.setItem('accessToken', data.access);
            localStorage.setItem('refreshToken', data.refresh);
            localStorage.setItem('username', variables.username);
            isAuth = true;
            toast.success("Log in successful");
            setTimeout(() => {
                navigate('/profile/' + variables.username + '/');
                setIsOpen(false);
                window.location.reload(); // reload to update the user context
            }, 500);
        },
        onError: (error) => {
            toast.error("Failed to login.");
        }
    });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: "",
      password: "",
    },
  })

  function onSubmit(values: z.infer<typeof formSchema>) {
    postLogin.mutate({ username:values.username , password:values.password  });
  }

    return (
      <>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        
        <Form {...form}>
          <DialogTrigger asChild>
            {children}
          </DialogTrigger>
          <DialogContent className="flex bg-black/40 backdrop-blur-md items-center justify-center dark text-white">
              <form onSubmit={form.handleSubmit(onSubmit)} className="">
            <DialogHeader className="flex flex-col items-center justify-center">
              <DialogTitle className="font-semibold text-xl">Login</DialogTitle>
            </DialogHeader>
          <section className="flex flex-col justify-center items-center my-4">
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>Username</FormLabel>
                  <FormControl>
                    <Input placeholder="coolman32" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input type="password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button className="flex w-full hover:cursor-pointer" type="submit">{isAuth ? <Check /> : "Login"}</Button>
          </section>

          
          
          <aside className="flex flex-col justify-center items-center text-sm">
              <p className="text-white hover:cursor-pointer hover:underline">Forgot password?</p>
              <p className="text-neutral-500">
                No account? {" "}
                  <RegisterDialog>
                    <span className="text-white hover:cursor-pointer hover:underline">
                      Make one
                    </span>
                  </RegisterDialog>
              </p>
          </aside>
          </form>
          </DialogContent>
        </Form>
      </Dialog>
    </>
  )
}

